import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  alertCriteriaRows,
  confirmEmailAlert,
  getManagedAlert,
  type ManagedAlert,
  pauseEmailAlert,
  resumeEmailAlert,
  unsubscribeEmailAlert,
} from '../lib/alerts';
import { Button } from './ui/Button';

type PageState = 'loading' | 'success' | 'error';

function tokenFromSearch(searchParams: URLSearchParams) {
  return searchParams.get('token') || '';
}

export function AlertConfirmPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<PageState>('loading');

  useEffect(() => {
    const token = tokenFromSearch(searchParams);
    if (!token) {
      setState('error');
      return;
    }
    confirmEmailAlert(token)
      .then(() => setState('success'))
      .catch(error => {
        console.error('Failed to confirm alert:', error);
        setState('error');
      });
  }, [searchParams]);

  return (
    <AlertShell
      title={state === 'success' ? 'Alert confirmed' : state === 'error' ? 'Could not confirm alert' : 'Confirming alert'}
      body={state === 'success'
        ? 'New matching rentals will be sent to your email.'
        : state === 'error'
          ? 'This confirmation link is invalid or expired.'
          : 'One moment.'}
    />
  );
}

export function AlertUnsubscribePage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<PageState>('loading');

  useEffect(() => {
    const token = tokenFromSearch(searchParams);
    if (!token) {
      setState('error');
      return;
    }
    unsubscribeEmailAlert(token)
      .then(() => setState('success'))
      .catch(error => {
        console.error('Failed to unsubscribe alert:', error);
        setState('error');
      });
  }, [searchParams]);

  return (
    <AlertShell
      title={state === 'success' ? 'Alert unsubscribed' : state === 'error' ? 'Could not unsubscribe alert' : 'Unsubscribing alert'}
      body={state === 'success'
        ? 'You will no longer receive emails for this alert.'
        : state === 'error'
          ? 'This unsubscribe link is invalid or expired.'
          : 'One moment.'}
    />
  );
}

export function AlertManagePage() {
  const [searchParams] = useSearchParams();
  const [alert, setAlert] = useState<ManagedAlert | null>(null);
  const [state, setState] = useState<PageState>('loading');
  const [actionStatus, setActionStatus] = useState<'idle' | 'saving'>('idle');
  const token = tokenFromSearch(searchParams);

  useEffect(() => {
    if (!token) {
      setState('error');
      return;
    }
    getManagedAlert(token)
      .then(result => {
        if (!result) {
          setState('error');
          return;
        }
        setAlert(result);
        setState('success');
      })
      .catch(error => {
        console.error('Failed to load alert:', error);
        setState('error');
      });
  }, [token]);

  const updateStatus = async (action: 'pause' | 'resume' | 'unsubscribe') => {
    setActionStatus('saving');
    try {
      const result = action === 'pause'
        ? await pauseEmailAlert(token)
        : action === 'resume'
          ? await resumeEmailAlert(token)
          : await unsubscribeEmailAlert(token);
      setAlert(previous => previous ? { ...previous, alert_status: result.alert_status } : previous);
    } catch (error) {
      console.error('Failed to update alert:', error);
      setState('error');
    } finally {
      setActionStatus('idle');
    }
  };

  if (state !== 'success' || !alert) {
    return (
      <AlertShell
        title={state === 'error' ? 'Could not load alert' : 'Loading alert'}
        body={state === 'error' ? 'This management link is invalid or expired.' : 'One moment.'}
      />
    );
  }

  const rows = alertCriteriaRows({
    location_mode: alert.location_mode,
    location_name: alert.location_name,
    bounds: alert.bounds,
    max_price_idr: alert.max_price_idr,
    min_bedrooms: alert.min_bedrooms,
    keywords: alert.keywords || [],
  });

  return (
    <main className="mx-auto flex min-h-screen max-w-xl flex-col justify-center px-6 py-12">
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-gray-900">Manage alert</h1>
        <dl className="mt-5 space-y-2 text-sm text-gray-700">
          {rows.map(row => (
            <div key={row.label} className="flex justify-between gap-4">
              <dt>{row.label}</dt>
              <dd className="text-right">{row.value}</dd>
            </div>
          ))}
          <div className="flex justify-between gap-4">
            <dt>Email</dt>
            <dd className="text-right">{alert.email}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt>Status</dt>
            <dd className="capitalize">{alert.alert_status}</dd>
          </div>
        </dl>
        <div className="mt-6 flex flex-wrap gap-3">
          {alert.alert_status === 'paused' ? (
            <Button disabled={actionStatus === 'saving'} onClick={() => updateStatus('resume')}>Resume</Button>
          ) : (
            <Button disabled={actionStatus === 'saving' || alert.alert_status === 'unsubscribed'} onClick={() => updateStatus('pause')}>Pause</Button>
          )}
          <Button
            variant="secondary"
            disabled={actionStatus === 'saving' || alert.alert_status === 'unsubscribed'}
            onClick={() => updateStatus('unsubscribe')}
          >
            Unsubscribe
          </Button>
          <Link className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800" to="/">Back to rentals</Link>
        </div>
      </div>
    </main>
  );
}

function AlertShell({ title, body }: { title: string; body: string }) {
  return (
    <main className="mx-auto flex min-h-screen max-w-lg flex-col justify-center px-6 py-12 text-center">
      <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
      <p className="mt-3 text-sm text-gray-600">{body}</p>
      <Link className="mt-6 text-sm font-medium text-blue-600 hover:text-blue-800" to="/">Back to rentals</Link>
    </main>
  );
}
